import classes from './styles.module.scss';
import { memo, useEffect, useState } from 'react';
import { Box, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid } from '@mui/material';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { IReducer } from 'redux/reducers';
import { DataFilter } from '..';
import InputSelect from './component/InputsSelect';
import TextTitle from './component/TextTitle';
import { ReportConfig } from 'interfaces/report';
import _ from 'lodash';
import CustomMultiYearPicker from 'components/DatesPicker';

interface ModalSettingReportProps {
  isOpen: boolean;
  onClose: () => void;
  dataFilter?: DataFilter;
  hiddenSubTableIds?: number[];
  setDataConfigReport: (data: ReportConfig[]) => void;
  dataConfigReport: ReportConfig[];
  onGenerateReport: (data: ReportConfig[], callback: () => void) => void;
  yearReport: number[];
  setYearReport: (data: number[]) => void;
}
export interface OptionItem<T = number> {
  id: T;
  name: string;
  title?: string;
  fullName?: string;
  translation?: any;
  img?: string;
  subName?: string;
  icon?: any;
  symbol?: string;
  result?: boolean;
}
interface ConfigFromData {
  // years: OptionItem[];
  topicId: OptionItem;
  variableIds: OptionItem[];
  dataShow: string[];
}
const schema = Yup.object().shape({
  // years: Yup.array(
  //   Yup.object().shape({
  //     id: Yup.number().required('Years is required.'),
  //     name: Yup.string().required(),
  //   })
  // )
  //   .max(5, 'You can select up to 5 years.')
  //   .min(1, 'At least one year is required.')
  //   .required('Years field is required.'),
  topicId: Yup.object()
    .shape({
      id: Yup.number().required('Topic is required.'),
      name: Yup.string().required(),
    })
    .required('Topic field is required'),
  variableIds: Yup.array(
    Yup.object().shape({
      id: Yup.number().required('Variable is required.'),
      name: Yup.string().required(),
    })
  )
    .max(5, 'You can select up to 5 variables.')
    .min(1, 'At least one year is required.')
    .required('Variable is required.'),
  dataShow: Yup.array().nullable().min(1, 'Data which to display is required.'),
});

const ModalSettingReport = memo(
  ({ isOpen, onClose, setDataConfigReport, dataFilter, onGenerateReport, dataConfigReport, yearReport, setYearReport }: ModalSettingReportProps) => {
    const [page, setPage] = useState<number>(1);
    const { dataControl } = useSelector((state: IReducer) => state.dataControl);
    const [optionVariable, setOptionVariable] = useState<OptionItem[]>([]);
    const [isSubmitForm, setIsSubmitForm] = useState<boolean>(false);

    const {
      handleSubmit,
      formState: { errors },
      reset,
      control,
      setValue,
      watch,
    } = useForm<ConfigFromData>({
      resolver: yupResolver(schema),
      mode: 'onChange',
      defaultValues: {
        dataShow: [],
      },
    });

    const _resetForm = () => {
      reset({
        // years: [],
        topicId: null,
        variableIds: [],
        dataShow: [],
      });
    };

    const addReportConfig = (data: ReportConfig) => {
      const updateDataReport = [...dataConfigReport];
      const _reportConfig = dataConfigReport?.findIndex((report) => report.page === data.page);
      if (_reportConfig !== -1) {
        updateDataReport[_reportConfig] = data;
        return updateDataReport;
      } else {
        updateDataReport.push(data);
      }
      setDataConfigReport(updateDataReport);
      return updateDataReport;
    };

    const onSubmit = async (data: ConfigFromData, event?: React.BaseSyntheticEvent) => {
      setIsSubmitForm(true);
      const submitter = (event?.nativeEvent as SubmitEvent | undefined)?.submitter as HTMLButtonElement | undefined;
      const submitterName = submitter?.name;
      const dataReport = await addReportConfig({ ...data, page: _.cloneDeep(page) });
      if (!yearReport?.length || yearReport?.length >= 6) return;
      if (submitterName === 'save-and-generate') {
        onGenerateReport(dataReport, _onClose);
      } else {
        const _page: number = page + 1;
        setPage(_page);
        _resetForm();
      }
    };

    const prePage = () => {
      const _page: number = page - 1;
      setPage(_page);
    };

    const _onClose = () => {
      setPage(1);
      onClose();
    };
    const _setOptionVariable = async (id: number) => {
      const _optionVariable: OptionItem[] = await dataControl?.topics
        ?.find((t) => t.id === id)
        ?.fields?.map((it) => ({ id: it.variableId, name: it.name }));
        await _optionVariable?.sort((a, b) => {
          return a.id - b.id;
        });
      setOptionVariable(_optionVariable ?? []);
    };

    useEffect(() => {
      const fetchData = async (_page: number) => {
        const getData = await dataConfigReport.find((it) => it.page === _page);
        if (getData) {
          await _setOptionVariable(getData.topicId.id);
          reset({
            // years: getData.years,
            variableIds: getData.variableIds,
            topicId: getData.topicId,
            dataShow: getData.dataShow,
          });
        }
      };
      fetchData(page);
    }, [page]);

    const selectedValues = watch('dataShow');

    const onChangeSelectTopic = (e: any) => {
      const selected: OptionItem = e as OptionItem;
      setValue('variableIds', []);
      if (selected?.id) {
        _setOptionVariable(selected.id);
        setValue('topicId', selected);
      }
    };
    const onChangeSelectName = (data: number[]) => {
      setYearReport(data.sort((a, b) => a - b));
    };
    return (
      <Dialog
        PaperProps={{ style: { overflowY: 'visible' } }}
        maxWidth={'md'}
        transitionDuration={0.3}
        open={isOpen}
        onClose={_onClose}
        className={classes.container}
        sx={{ overflow: 'hidden' }}
      >
        <DialogTitle className={classes.title} textAlign={'center'}>
          Configs report
        </DialogTitle>
        <form autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ overflowY: { md: 'visible' } }}>
            <Box width={'100%'}>
              <Box className={classes.formInputYear}>
                <Grid container columnSpacing={6}>
                  <Grid item xs={12} sm={12}>
                    <CustomMultiYearPicker
                      label="Year report"
                      value={yearReport}
                      onChange={(newYears) => onChangeSelectName(newYears)}
                      yearOptions={dataFilter?.yearRange}
                      errorMessage={
                        isSubmitForm && (!yearReport?.length ? 'Year is required!' : yearReport?.length >= 6 ? 'You can select up to 5 years.' : null)
                      }
                    />
                    {/* <InputSelect
                  value={yearReport}
                  title="Year report"
                  name="years"
                  onChange={onChangeSelectName}
                  fullWidth
                  selectProps={{
                    isMulti: true,
                    options: optionsYear ?? [],
                  }}
                  errorMessage={isSubmitForm && (!yearReport?.length ? 'Year is required!' : (yearReport?.length >= 6 ? "You can select up to 5 years." : null))}
                /> */}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box width={'100%'} pt={1}>
              <Card sx={{ borderRadius: '10px' }}>
                <CardContent className={classes.content} sx={{ padding: '20px 0' }}>
                  <Box width={'100%'} textAlign={'center'}>
                    <strong>Config page {page}</strong>
                  </Box>
                  <Box width={'100%'}>
                    <Grid container columnSpacing={6}>
                      <Grid item xs={12} sm={12}>
                        <InputSelect
                          title="Topic"
                          name="topicId"
                          control={control}
                          fullWidth
                          selectProps={{
                            options: (dataControl?.topics ?? [])
                              .filter((topic) => dataFilter?.topic?.includes(topic.id))
                              .map((topic) => ({
                                id: topic.id,
                                name: topic.name,
                              })),
                            onChange: (e) => {
                              onChangeSelectTopic(e);
                            },
                          }}
                          errorMessage={(errors.topicId as any)?.id?.message}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputSelect
                          title="Variables"
                          name="variableIds"
                          control={control}
                          fullWidth
                          selectProps={{
                            isMulti: true,
                            options: optionVariable ?? [],
                          }}
                          errorMessage={(errors.variableIds as any)?.message}
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={12}>
                      <InputSelect
                        title="Years"
                        name="years"
                        control={control}
                        fullWidth
                        selectProps={{
                          isMulti: true,
                          options: optionsYear ?? [],
                        }}
                        errorMessage={(errors.years as any)?.message}
                      />
                    </Grid> */}
                      <Grid item xs={12} sm={6}>
                        <TextTitle>Data which to display</TextTitle>
                        <Controller
                          name="dataShow"
                          control={control}
                          render={({ field }) => (
                            <>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    value="BASE"
                                    disabled={!dataFilter?.subTable?.includes(1)}
                                    checked={selectedValues.includes('BASE')}
                                    onChange={(e) => {
                                      const newValue = e.target.checked ? [...field.value, 'BASE'] : field.value.filter((val) => val !== 'BASE');
                                      field.onChange(newValue);
                                    }}
                                  />
                                }
                                label="Base case"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    value="SCENARIO"
                                    disabled={!dataFilter?.subTable?.includes(2)}
                                    checked={selectedValues.includes('SCENARIO')}
                                    onChange={(e) => {
                                      const newValue = e.target.checked ? [...field.value, 'SCENARIO'] : field.value.filter((val) => val !== 'SCENARIO');
                                      field.onChange(newValue);
                                    }}
                                  />
                                }
                                label="Scenario"
                              />
                            </>
                          )}
                        />
                        {(errors.dataShow as any)?.message && <p className={classes.textError}>{errors.dataShow.message}</p>}
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </DialogContent>

          <DialogActions sx={{ mt: 10 }} className={classes.dialogAction}>
            <Box display={'flex'} gap={2} justifyContent={'space-between'} width={'100%'} sx={{ ml: 'auto' }}>
              <SecondaryButton disabled={!!(page <= 1)} type="button" onClick={prePage}>
                Previous page
              </SecondaryButton>
              <PrimaryButton name="save-and-generate" type="submit">
                Finished - Generate Report
              </PrimaryButton>
              <SecondaryButton name="save-and-next" disabled={!!(page >= 9)} type="submit">
                Save & Next page
              </SecondaryButton>
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    );
  }
);
export default ModalSettingReport;
